import { SearchModes } from '@/domainLogic/index.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { getSearchParam } from '@/app/common/urlParametersUtils.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { areTaxesIncludedInPrice } from '@/app/common/config/configuration/configurationUtilsClient.ts';
import { Text } from '@lmn/design-system';
import { replaceVar } from '../../../../utils/misc';
import type { DealCard } from '../../../../utils/buildDealCards/dealCardTypes';
import { replacePlaceholdersWithBoldTags, replacePlaceholdersWithVars } from '../../../../utils/translations';
import { formatPrice } from '../../../../utils/formatting';
import { TaxesModalSection, TaxesModalContainer, TaxesModalAllIncludedSection, Separator } from './styled';

type Props = {
  dealCard: DealCard;
  showTotalPrice: boolean;
  travellersCount: number;
  searchParams: SearchParams;
};

const getPriceWithoutTaxes = (dealCard: DealCard, showTotalPrice: boolean, taxesIncluded: boolean) => {
  const totalPrice = dealCard.product.rate.price.total.price;
  const { taxes, taxesTotal, price } = dealCard.product.rate.price;

  if (showTotalPrice) return taxesIncluded ? totalPrice - taxesTotal : totalPrice;
  return taxesIncluded ? price - taxes : price;
};

type TaxesAllIncludedContentProps = {
  priceLabel: string;
  priceWithoutTaxes: string;
  taxesLabel: string;
  taxesPrice: string;
  totalLabel: string;
  totalPrice: string;
  explanation: string;
  ppSuffix?: string;
};

const withSuffix = (value: string, suffix?: string) => (suffix ? `${value} ${suffix}` : value);

export function TaxesAllIncludedContent({
  priceLabel,
  priceWithoutTaxes,
  taxesLabel,
  taxesPrice,
  totalLabel,
  totalPrice,
  explanation,
  ppSuffix,
}: TaxesAllIncludedContentProps) {
  return (
    <TaxesModalContainer>
      <TaxesModalAllIncludedSection>
        <Text variant="BodySmall">{priceLabel}</Text>
        <Text variant="BodySmall">{withSuffix(priceWithoutTaxes, ppSuffix)}</Text>
      </TaxesModalAllIncludedSection>
      <TaxesModalAllIncludedSection>
        <Text variant="BodySmall">{taxesLabel}</Text>
        <Text variant="BodySmall">{withSuffix(taxesPrice, ppSuffix)}</Text>
      </TaxesModalAllIncludedSection>
      <Separator />
      <TaxesModalAllIncludedSection>
        <Text variant="BodySmall" weight="bold">
          {totalLabel}
        </Text>
        <Text variant="BodySmall" weight="bold">
          {withSuffix(totalPrice, ppSuffix)}
        </Text>
      </TaxesModalAllIncludedSection>
      <Text variant="BodySmall" color="textSecondary">
        {explanation}
      </Text>
    </TaxesModalContainer>
  );
}

const TaxesModal = ({ dealCard, showTotalPrice, searchParams, travellersCount }: Props) => {
  const { localization, searchInfoData } = usePageWrapperData();

  const { country, configValuesApp } = localization;
  const { t } = useTranslation();
  const searchMode = getSearchParam(searchParams, URLParameters.SEARCH_MODE);
  const taxesTooltipHOPrice = t('vacation.oss.cards.taxes_tooltip.ho_price');
  const taxesTooltipDPPrice = t('vacation.oss.cards.taxes_tooltip.dp_price');
  const taxesTooltipTHPrice = t('vacation.oss.cards.taxes_tooltip.th_price');
  const taxesTooltipTaxes = t('vacation.oss.cards.taxes_tooltip.taxes');
  const taxesTooltipTotal = t('vacation.oss.cards.taxes_tooltip.total');
  const taxesTooltipTotalRooms = t('vacation.oss.cards.taxes_tooltip.total_rooms');
  const taxesTooltipAllIncludedFlightHotel = t('vacation.common.flightplushotel');
  const taxesTooltipAllIncludedTaxesFees = `${t('vacation.common.citytaxes.taxes')} ${t('vacation.common.citytaxes.payablehotel')}`;
  const taxesTooltipAllIncludedTotal = t('vacation.common.price.total');
  const taxesTooltipAllIncludedExplanation = t('vacation.oss.cards.taxes_fees_included.all.desc');
  const taxesTooltipHOExplanationTaxesIncluded = t('vacation.oss.cards.taxes_tooltip.ho_explanation_taxes_included');
  const taxesTooltipHOExplanationTaxesExcluded = t('vacation.oss.cards.taxes_tooltip.ho_explanation_taxes_excluded');
  const taxesTooltipDpExplanationTaxesIncluded = t('vacation.oss.cards.taxes_tooltip.dp_explanation_taxes_included');
  const taxesTooltipDpExplanationTaxesExcluded = t('vacation.oss.cards.taxes_tooltip.dp_explanation_taxes_excluded');
  const isDP = searchInfoData.searchMode === SearchModes.DP;
  const isTaxesIncludedDoublePricing = isDP && localization?.configValuesApp?.taxesIncludedDoublePricing;

  const {
    dates: { nights },
    rate: {
      price: { taxes, taxesTotal, price, isPerPerson },
      features: { rooms: numRooms },
    },
  } = dealCard.product;
  const isHO = searchMode === SearchModes.HO;
  const isTH = searchMode === SearchModes.TH;
  const taxesIncluded = areTaxesIncludedInPrice({ searchMode, country, configValuesApp });
  const taxesIncludedPopup = taxesIncluded && localization?.configValuesApp?.taxesIncludedPopup;
  const isFamily = configValuesApp?.doublePriceFamilyEnabled ?? false;
  // Family double-pricing: mirror the card's per-person × travellers calculation
  // so the modal breakdown total matches the card label and hotel-detail page.
  const useFamilyDoublePricing = isPerPerson && isFamily && isTaxesIncludedDoublePricing;
  const priceWithoutTaxes = useFamilyDoublePricing
    ? price * travellersCount
    : getPriceWithoutTaxes(dealCard, showTotalPrice, taxesIncluded);
  const taxesAmount = useFamilyDoublePricing ? taxes * travellersCount : showTotalPrice ? taxesTotal : taxes;

  const priceWithTaxesFormatted = formatPrice({
    price: priceWithoutTaxes + taxesAmount,
    localization,
  });
  const priceWithoutTaxesFormatted = formatPrice({
    price: priceWithoutTaxes,
    localization,
  });
  const taxesPriceFormatted = formatPrice({
    price: taxesAmount,
    localization,
  });
  const totalPriceFormattedHO = formatPrice({
    price: taxesIncluded
      ? dealCard.product.rate.price.total.price
      : dealCard.product.rate.price.total.price + taxesTotal,
    localization,
  });
  const titleLabel = isHO
    ? replacePlaceholdersWithBoldTags(taxesTooltipHOPrice)
    : isTH
      ? replacePlaceholdersWithBoldTags(taxesTooltipTHPrice)
      : replacePlaceholdersWithBoldTags(taxesTooltipDPPrice);

  const totalKey = numRooms > 1 ? taxesTooltipTotalRooms : taxesTooltipTotal;
  const totalLabel = replaceVar(replacePlaceholdersWithVars(totalKey), {
    ...(nights ? { nights: nights?.toString() } : {}),
    nights_label:
      nights && nights > 1
        ? t('vacation.oss.header.widget.calendar.nights')
        : t('vacation.oss.header.widget.calendar.night'),
    travellers: travellersCount.toString(),
    travellers_label:
      travellersCount > 1
        ? t('vacation.oss.header.widget.people.travellers')
        : t('vacation.oss.header.widget.people.traveller'),
    rooms: numRooms.toString(),
  });
  const taxesLabelHTML = replacePlaceholdersWithBoldTags(taxesTooltipTaxes);
  const totalLabelHTML = replacePlaceholdersWithBoldTags(totalLabel);
  const getHoExplanation = (areTaxesIncluded: boolean) => {
    if (areTaxesIncluded) return taxesTooltipHOExplanationTaxesIncluded;
    return taxesTooltipHOExplanationTaxesExcluded;
  };
  const getDpExplanation = (areTaxesIncluded: boolean) => {
    if (areTaxesIncluded) return taxesTooltipDpExplanationTaxesIncluded;
    return taxesTooltipDpExplanationTaxesExcluded;
  };

  return taxesIncludedPopup || isTaxesIncludedDoublePricing ? (
    <TaxesAllIncludedContent
      priceLabel={taxesTooltipAllIncludedFlightHotel}
      priceWithoutTaxes={priceWithoutTaxesFormatted}
      taxesLabel={taxesTooltipAllIncludedTaxesFees}
      taxesPrice={taxesPriceFormatted}
      totalLabel={taxesTooltipAllIncludedTotal}
      totalPrice={priceWithTaxesFormatted}
      explanation={taxesTooltipAllIncludedExplanation}
      ppSuffix={showTotalPrice ? undefined : t('vacation.oss.cards.pp')}
    />
  ) : (
    <TaxesModalContainer>
      <TaxesModalSection>
        <div
          dangerouslySetInnerHTML={{
            __html: titleLabel,
          }}
        />
        <div>{priceWithoutTaxesFormatted}</div>
      </TaxesModalSection>
      <TaxesModalSection>
        <div
          dangerouslySetInnerHTML={{
            __html: taxesLabelHTML,
          }}
        />
        <div>{taxesPriceFormatted}</div>
      </TaxesModalSection>
      {isHO && (
        <TaxesModalSection>
          <div
            dangerouslySetInnerHTML={{
              __html: totalLabelHTML,
            }}
          />
          <div>{totalPriceFormattedHO}</div>
        </TaxesModalSection>
      )}
      <div>{isHO ? getHoExplanation(taxesIncluded) : getDpExplanation(taxesIncluded)}</div>
    </TaxesModalContainer>
  );
};

export default TaxesModal;
